import React, {memo, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, fontSize, fontWeight} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

interface ProcessStep {
    title: string;
    paragraphs: string[];
}

interface MvpProcessProps {
    heading: string;
    subheading: string;
    steps: ProcessStep[];
}

const stickyColSx = {position: {md: 'sticky'}, top: {md: 120}} as const;

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.primary,
        mb: 1.5
    }
] as const;

const subheadingSx = {color: colors.text.primary, lineHeight: 1.9} as const;

const timelineSx = {position: 'relative', pl: {xs: 3, md: 4}} as const;

// The rail is the unfilled track; the progress bar on top of it grows as steps are reached.
const railSx = {position: 'absolute', left: 5, top: 8, bottom: 8, width: 2, bgcolor: colors.neutral.borderLight} as const;

const railFillSx = (percent: number) =>
    ({
        position: 'absolute',
        left: 5,
        top: 8,
        width: 2,
        height: `${percent}%`,
        bgcolor: colors.primary.link,
        transition: 'height 0.5s ease'
    } as const);

const stepSx = (reached: boolean) =>
    ({
        position: 'relative',
        pb: {xs: 4, md: 6},
        opacity: reached ? 1 : 0.35,
        transition: 'opacity 0.4s ease'
    } as const);

const dotSx = (reached: boolean) =>
    ({
        position: 'absolute',
        // Offsets keep the dot's centre on the 2px rail at each breakpoint's padding.
        left: {xs: -28, md: -36},
        top: 2,
        width: 20,
        height: 20,
        borderRadius: borderRadius.circle,
        bgcolor: colors.background.default,
        border: `5px solid ${reached ? colors.primary.link : colors.neutral.borderLight}`,
        transition: 'border-color 0.4s ease'
    } as const);

const stepNumberSx = {
    color: colors.accent.orange,
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: '0.04em',
    mb: 0.5
} as const;

const stepTitleSx = {color: colors.text.primary, fontSize: fontSize[18], fontWeight: fontWeight.bold, mb: 1.5} as const;

const stepParagraphSx = {color: colors.text.primary, fontSize: fontSize[14], lineHeight: 1.8, mb: 1.5} as const;

const MvpProcess = ({heading, subheading, steps}: MvpProcessProps) => {
    const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [reachedIndex, setReachedIndex] = useState(0);

    useEffect(() => {
        const nodes = stepRefs.current.filter(Boolean) as HTMLDivElement[];
        if (!nodes.length) return undefined;

        // The band sits just above the viewport's middle: a step counts as reached once
        // its heading scrolls into that line, so the rail fills in step with the reading.
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const index = nodes.indexOf(entry.target as HTMLDivElement);
                    if (index >= 0) setReachedIndex(index);
                });
            },
            {rootMargin: '-45% 0px -50% 0px', threshold: 0}
        );

        nodes.forEach((node) => observer.observe(node));
        return () => observer.disconnect();
    }, [steps.length]);

    const fillPercent = steps.length > 1 ? (reachedIndex / (steps.length - 1)) * 100 : 100;

    return (
        <Box>
            <MUIContainer>
                <Grid container spacing={{xs: 4, md: 6}}>
                    <Grid item xs={12} md={4}>
                        <Box sx={stickyColSx}>
                            <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                                {heading}
                            </Typography>
                            <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                                {parse(subheading)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Box sx={timelineSx}>
                            <Box sx={railSx} aria-hidden="true" />
                            <Box sx={railFillSx(fillPercent)} aria-hidden="true" />

                            {steps.map((step, index) => (
                                <Box
                                    key={step.title}
                                    ref={(node: HTMLDivElement | null) => {
                                        stepRefs.current[index] = node;
                                    }}
                                    sx={stepSx(index <= reachedIndex)}
                                >
                                    <Box sx={dotSx(index <= reachedIndex)} />
                                    <Typography component="p" className="font-headers" sx={stepNumberSx}>
                                        {String(index + 1).padStart(2, '0')}
                                    </Typography>
                                    <Typography component="h3" className="font-headers" sx={stepTitleSx}>
                                        {step.title}
                                    </Typography>
                                    {step.paragraphs.map((paragraph) => (
                                        <Typography key={paragraph} component="p" className="font-regular-text" sx={stepParagraphSx}>
                                            {parse(paragraph)}
                                        </Typography>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpProcess);
